import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoutConfirmModal from "../LogoutConfirmModal";
import { img } from "../../constants/assets";
import { isAdmin, isPartner, getDashboardHomePath } from "../../utils/auth";

const navItems = [
  {
    to: "/dashboard",
    labelKey: "dashboard.sidebar.overview",
    end: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    to: "/dashboard/esims",
    labelKey: "dashboard.sidebar.esims",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 7h6M9 11h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: "/dashboard/orders",
    labelKey: "dashboard.sidebar.orders",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 9l9-6 9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9Z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    to: "/dashboard/customers",
    labelKey: "dashboard.sidebar.customers",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M14.5 20c.4-2.2 2-3.5 4-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: "/dashboard/appointments",
    labelKey: "dashboard.sidebar.appointments",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: "/dashboard/partnership",
    labelKey: "dashboard.sidebar.partnership",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M7 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM17 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    to: "/dashboard/contact",
    labelKey: "dashboard.sidebar.contact",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="m22 6-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: "/dashboard/documentation",
    labelKey: "dashboard.sidebar.documentation",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M6 4h9l3 3v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M15 4v4h4M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const partnerNavItems = navItems.filter((item) =>
  ["/dashboard/esims", "/dashboard/orders", "/dashboard/documentation"].includes(item.to)
);

function DashboardSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const visibleNavItems = isAdmin() ? navItems : isPartner() ? partnerNavItems : [];
  const showSettings = isAdmin() || isPartner();

  return (
    <aside className="dashboard-sidebar">
      <NavLink className="dashboard-sidebar__logo" to={getDashboardHomePath()}>
        <img src={img("logo.svg")} alt="R4L Solutions" />
      </NavLink>

      <nav className="dashboard-sidebar__nav">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `dashboard-sidebar__link${isActive ? " dashboard-sidebar__link--active" : ""}`
            }
          >
            <span className="dashboard-sidebar__link-icon">{item.icon}</span>
            {t(item.labelKey)}
          </NavLink>
        ))}
        {showSettings ? (
          <NavLink
            to="/dashboard/settings"
            className={({ isActive }) =>
              `dashboard-sidebar__link${isActive ? " dashboard-sidebar__link--active" : ""}`
            }
          >
            <span className="dashboard-sidebar__link-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {t("dashboard.sidebar.settings")}
          </NavLink>
        ) : null}
        <button
          type="button"
          className="dashboard-sidebar__link"
          onClick={() => setIsLogoutModalOpen(true)}
        >
          <span className="dashboard-sidebar__link-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 17l5-5-5-5M21 12H9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          {t("dashboard.sidebar.logout")}
        </button>
      </nav>

      <div className="dashboard-sidebar__support">
        <div className="dashboard-sidebar__support-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M6 20c0-3.3 2.7-6 6-6s6 2.7 6 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div>
          <p className="dashboard-sidebar__support-title">{t("dashboard.sidebar.needHelp")}</p>
          <a className="dashboard-sidebar__support-link" href="mailto:support@roam4less.com">
            {t("dashboard.sidebar.contactSupport")}
          </a>
        </div>
      </div>

      <LogoutConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onSuccess={() => navigate("/login")}
      />
    </aside>
  );
}

export default DashboardSidebar;
